(ns app.front.view.application-wizard.core
  "Guided application wizard flow that enables applicants to complete and submit their initial application package."
  (:require [app.domain.address.model :as address-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.project.model :as project-model]
            [app.domain.property.model :as property-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.form.subs :as form-subs]
            [app.front.process :as process]
            [app.front.process.save-tracking :as save-tracking]
            [app.front.requirement.components :as req-components]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as action-buttons]
            [app.front.ui.section :as section]
            [app.front.ui.v2.mobile-bottom-action-bar :as mobile-bar]
            [app.front.view.application-wizard.attachments :as attachments-step]
            [app.front.view.application-wizard.authority-create :as authority-create]
            [app.front.view.application-wizard.classification :as classification-step]
            [app.front.view.application-wizard.forms :as forms-step]
            [app.front.view.application-wizard.location :as location-step]
            [app.front.view.application-wizard.navigation :as wizard-nav]
            [app.front.view.application-wizard.parties :as parties-step]
            [app.front.view.application-wizard.payment :as payment-step]
            [app.front.view.application-wizard.plan :as plan-step]
            [app.front.view.application-wizard.renewal-overview :as renewal-overview]
            [app.front.view.application-wizard.save-status :as save-status]
            [app.front.view.application-wizard.step-tracker :as step-tracker]
            [app.front.view.application-wizard.subs :as subs]
            [app.front.view.application-wizard.summary :as summary-step]
            [app.front.view.property.units-editor :as units-editor]
            [app.front.view.wizard.property-selector :as property-selector]
            [app.front.view.workspace.subs :as ws-subs]
            [app.front.view.workspace.util :as workspace-util]
            [app.i18n.model :as i18n-model]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]
            [schema-tools.core :as st]
            [schema.core :as s]))

(defn- wizard-entry-query-params [query-params]
  (select-keys query-params [:country :municipality :permit-type :group-id]))

(rf/reg-event-db ::store-query-params
  (fn [db [_ query-params]]
    (assoc db ::wizard-query-params
           (wizard-entry-query-params (or query-params (-> db :route/match :parameters :query))))))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ authority-group-id permit-type municipality]]
    {:db (dissoc db ::visited-step-paths)
     :fx (cond-> [[:dispatch [::load-wizard-config authority-group-id permit-type municipality]]]
           (not (project-model/permit-types-without-project permit-type))
           (conj [:dispatch [::plan-step/load-user-projects]]))}))

(rf/reg-event-fx ::set-workspace-data-for-step
  (fn [{:keys [db]} [_ workspace-id]]
    (let [workspace          (entities/find-entity (entities/db->context db) {:workspace/id workspace-id})
          project            (workspace-model/project workspace)
          property           (workspace-model/single-property workspace)
          location           (or (:entity/location property) (:entity/location workspace))
          municipality       (-> workspace :workspace/municipality :municipality/number)
          subdivision        (-> workspace :workspace/municipality :municipality/subdivision)
          country            (-> workspace :workspace/municipality :municipality/country)
          address            (-> workspace :workspace/structural-address
                                 (select-keys [:address/full-address :address/street-name :address/street-number
                                               :address/street-letter :address/postal-code :address/city]))
          location-data      (p/assoc-if (select-keys property property-model/property-creation-keys)
                                         :x (:location.center/x location)
                                         :y (:location.center/y location)
                                         :geometry (:location/shape location)
                                         :municipality municipality
                                         :subdivision (or subdivision country)
                                         :address (not-empty address))
          app                (workspace-model/workspace-application workspace)
          category-id        (-> (or (:application/category-group-config app)
                                     (:workspace/category-group-config workspace))
                                 :db/id)
          work-types         (->> (or (:application/work-type-group-configs app)
                                      (:workspace/work-type-group-configs workspace))
                                  (mapv :db/id)
                                  not-empty)
          work-target        (-> (or (:application/work-target-group-config app)
                                     (:workspace/work-target-group-config workspace))
                                 :db/id)
          query-params       (p/assoc-if (-> db :route/match :parameters :query)
                                         :permit-type (:workspace/permit-type workspace)
                                         :group-id (-> workspace :domain/authority-group :db/id)
                                         :country country
                                         :municipality municipality)
          pre-workspace?     (= :view/application-wizard (-> db :route/match :data :name))
          authority-group    (:domain/authority-group workspace)
          authority-group-id (:db/id authority-group)
          permit-type        (:workspace/permit-type workspace)
          ;; view-data mimics the :view/workspace map written by app.front.view.application/workspace-fetch-success
          ;; so that the process-states lookup works which the options menu items are derived from.
          ;; This is needed for options to be available in pre-workspace-steps since the pre-workspace route is not
          ;; nested under workspace/:workspace-id, so the canonical fetch never runs there.
          view-data          (assoc (workspace-util/workspace-view-data workspace app nil authority-group)
                                    :fetch-status :success)
          process-flow       (config-model/resolve-value :group.config/process-flow workspace)
          fx                 (cond-> []
                               (and country permit-type process-flow)
                               (conj [:dispatch [::process/fetch-process-states country permit-type process-flow]])

                               ;; When navigating to pre-workspace wizard steps with an existing workspace,
                               ;; load the wizard config directly (::init is only called for post-workspace routes)
                               pre-workspace?
                               (conj [:dispatch [::load-wizard-config authority-group-id permit-type municipality]])

                               (not pre-workspace?)
                               (conj [:dispatch [::init authority-group-id permit-type municipality]])

                               (:db/id project)
                               (conj [:dispatch [::plan-step/select-project (:db/id project)]])

                               (:project/name project)
                               (conj [:dispatch [::plan-step/set-project-name (:project/name project)]])

                               (:workspace/description-of-work workspace)
                               (conj [:dispatch [::plan-step/set-description-of-work (:workspace/description-of-work workspace)]])

                               (seq location-data)
                               (conj [:dispatch [::location-step/set-location-data location-data]])

                               (or category-id work-types work-target)
                               (conj [:dispatch [::classification-step/set-classification
                                                 (p/assoc-if {:category category-id}
                                                             :work-types work-types
                                                             :work-target work-target)]]))]
      (p/assoc-if {:db (-> db
                           (assoc ::wizard-query-params (wizard-entry-query-params query-params))
                           (assoc :view/workspace view-data))
                   :fx fx}
                  :route/replace (when pre-workspace?
                                   {:route  :view/application-wizard
                                    :params {:step (-> db :route/match :parameters :path :step)}
                                    :query  query-params})))))

(rf/reg-event-fx ::fetch-and-backfill-workspace
  (fn [_ [_ workspace-id]]
    {:comm/command {:command      :workspace/get
                    :data         {:workspace/id workspace-id
                                   :find-related [:entity.type/attachment
                                                  :entity.type/file
                                                  :entity.type/form
                                                  :entity.type/party
                                                  :entity.type/requirement]}
                    :on-success   [::fetch-and-backfill-workspace.success workspace-id]
                    :on-not-found [:route/push :dashboard]}}))

(rf/reg-event-fx ::fetch-and-backfill-workspace.success
  (fn [_ [_ workspace-id status data]]
    {:fx [[:dispatch [::entities/merge-entity-db status (:entities data)]]
          [:dispatch [::set-workspace-data-for-step workspace-id]]]}))

(rf/reg-event-fx ::load-wizard-config
  (fn [{:keys [db]} [_ authority-group-id permit-type municipality]]
    (when (and authority-group-id permit-type)
      (let [query-params          (-> db :route/match :parameters :query)
            selected-municipality (or municipality (:municipality query-params))
            selected-country      (:country query-params)]
        {:dispatch [:comm/command-monitored
                    {:command    :municipality/wizard-config-by-authority-group
                     :data       (cond-> (util/mmap authority-group-id permit-type)
                                   (and selected-country selected-municipality)
                                   (assoc :country selected-country
                                          :municipality selected-municipality))
                     :method     :get
                     :on-success [::load-wizard-config.success selected-municipality]}]}))))

(rf/reg-event-fx ::load-wizard-config.success
  (fn [{:keys [db]} [_ selected-number status {:keys [permit-types entities municipalities domain-group-id]}]]
    (let [selected-municipality (p/find-first municipalities {:municipality/number selected-number})]
      {:db (-> db
               ;; The response's :domain-group-id is the domain group of the authority group the
               ;; wizard was entered with. :permit-types can contain other authority groups serving
               ;; the same municipality, each with their own domain group, so this is only a fallback
               ;; until a category is selected. See `subs/domain-group-id`.
               (assoc ::permit-type->groups            permit-types
                      ::entry-domain-group-id          domain-group-id
                      ::authority-group-municipalities municipalities
                      ::municipality-name              (:municipality/name selected-municipality)))
       :fx (concat
             [[:dispatch [::entities/merge-entity-db status entities]]]
             (when (and (nil? selected-municipality) (= 1 (count municipalities)))
               [[:dispatch [::municipality-selected (first municipalities) {:replace? true}]]])
             (when (= "location" (-> db :route/match :parameters :path :step))
               [[:dispatch [::location-step/enter-step]]]))})))


(rf/reg-sub ::authority-group-municipalities
  :-> ::authority-group-municipalities)


(rf/reg-sub ::needs-municipality-selection?
  :<- [::authority-group-municipalities]
  :<- [:route/match]
  (fn [[munis match] _]
    (let [selected-number (-> match :parameters :query :municipality)]
      (and (> (count munis) 1)
           (not (p/find-first munis {:municipality/number selected-number}))))))


(rf/reg-event-fx ::municipality-selected
  ;; Writes the chosen municipality's country + municipality number into the current
  ;; route query and records the localised name so the location step can display it.
  ;; Use :municipality/country (e.g. :CA), not :municipality/subdivision (e.g. :CA-MB),
  ;; so the route keeps its documented country value.
  ;; opts: {:replace? bool}  — use :route/replace to avoid a new history entry when
  ;; auto-selecting (single-municipality group) on first load.
  (fn [{:keys [db]} [_ {:municipality/keys [number country name]} {:keys [replace?]}]]
    (let [match         (:route/match db)
          step          (-> match :parameters :path :step)
          existing-q    (-> match :parameters :query)
          new-q         (assoc existing-q
                               :municipality number
                               :country      country)
          route-fx-type (if replace? :route/replace :route/push)]
      {:db           (assoc db ::municipality-name name)
       route-fx-type {:route  :view/application-wizard
                      :params {:step step}
                      :query  new-q}})))


;; Steps before workspace creation — use query-param based route
(def pre-workspace-steps
  [{:path     "plan"
    :view     plan-step/view
    :label-tr [:application-wizard/plan]}
   {:path           "classification"
    :view           classification-step/view
    :description-tr [:application-wizard/classification-description]
    :label-tr       [:application-wizard/classification]}
   {:path             "location"
    :view             location-step/view
    :description-view location-step/description
    :label-tr         [:application-wizard/location]}])

;; Steps after workspace creation — use workspace path-param route
(def post-workspace-steps
  [{:path           "parties"
    :view           parties-step/view
    :description-tr [:application-wizard/parties-description]
    :label-tr       [:application-wizard/parties]}
   {:path           "forms"
    :view           forms-step/view
    :description-tr [:wizard/forms-description]
    :label-tr       [:application-wizard/forms]}
   {:path           "attachments"
    :view           attachments-step/view
    :description-tr [:application-wizard/attachments-description]
    :label-tr       [:application-wizard/attachments]}
   {:path           "summary"
    :view           summary-step/view
    :description-tr [:application-wizard/summary-description]
    :label-tr       [:application-wizard/summary]}])

(def renewal-overview-step
  {:path             wizard-nav/renewal-overview-step-path
   :view             renewal-overview/view
   :description-view renewal-overview/description
   :label-tr         [:application-wizard/overview]})

;; Optional step appended after summary only when there are unpaid upfront/deposit
;; bills, so the user can pay them inside the wizard before submitting.
(def payment-wizard-step
  {:path           "payment"
   :view           payment-step/view
   :description-tr [:application-wizard/payment-description]
   :label-tr       [:application-wizard/payment]})

(def standard-steps-with-payment (-> (into pre-workspace-steps post-workspace-steps)
                                     (conj payment-wizard-step)))

(def pre-workspace-step-paths (set (map :path pre-workspace-steps)))

(def post-workspace-step-paths (conj (set (map :path post-workspace-steps))
                                     (:path renewal-overview-step)
                                     (:path payment-wizard-step)))

(def first-step-path (-> pre-workspace-steps first :path))

(defn available-step-configs [location-not-required? create-pre-consultation? payment-required? renewal?]
  (let [steps (cond
                renewal?
                (cond-> (into [renewal-overview-step] post-workspace-steps)
                  payment-required? (conj payment-wizard-step))

                create-pre-consultation?
                pre-workspace-steps

                payment-required?
                standard-steps-with-payment

                :else
                (into pre-workspace-steps post-workspace-steps))]
    (if location-not-required?
      (remove #(= (:path %) "location") steps)
      steps)))

(rf/reg-event-db ::mark-step-visited
  (fn [db [_ step-path]]
    (update db ::visited-step-paths (fnil conj #{}) step-path)))

(rf/reg-sub ::visited-step-paths
  :-> ::visited-step-paths)

(rf/reg-sub ::step-visited?
  :<- [:route/match]
  :<- [::visited-step-paths]
  (fn [[match visited-step-paths] [_ step-path]]
    (or (= step-path (-> match :parameters :path :step))
        (contains? visited-step-paths step-path))))

(defn- post-workspace-step-completion [parties-completed? forms-completed? attachments-completed?]
  {wizard-nav/renewal-overview-step-path true
   "parties"                             parties-completed?
   "forms"                               forms-completed?
   "attachments"                         attachments-completed?})

(rf/reg-sub ::location-not-required?
  :<- [::classification-step/selected-category-id]
  :<- [::classification-step/selected-work-type-ids]
  :<- [::classification-step/selected-work-target-id]
  :<- [::entities/context]
  (fn [[category work-types work-target ctx] _]
    (classification-step/location-not-required? ctx category work-types work-target)))

(rf/reg-sub ::payment-required?
  ;; Whether the optional payment step is needed. True once there are unpaid
  ;; upfront/deposit bills, and it stays true while the application sits in
  ;; pre-submit-payment (bills paid but not yet submitted) so the step remains
  ;; visible for the user to submit. Defaults to false so the step is hidden
  ;; until bills exist (they are created after the application).
  :<- [::subs/application]
  (fn [application _]
    (boolean
      (or (= :state.application/pre-submit-payment (:domain/state application))
          (->> (summary-step/upfront-and-deposit-bills application)
               (filter (du/in-state-fn :bill.state/unpaid :bill.state/pending-confirmation))
               seq)))))

(rf/reg-sub ::available-steps
  :<- [::location-not-required?]
  :<- [::classification-step/create-pre-consultation?]
  :<- [::payment-required?]
  :<- [::subs/renewal-workspace?]
  (fn [[location-not-required? create-pre-consultation? payment-required? renewal?] _]
    (available-step-configs location-not-required?
                            create-pre-consultation?
                            payment-required?
                            renewal?)))

(rf/reg-event-fx ::open-step
  (fn [{:keys [db]} [_ {:keys [step-path ws-id replace?]}]]
    (let [query-params (or (-> db :route/match :parameters :query)
                           (::wizard-query-params db))
          workspace-id (or ws-id
                           (some-> db :route/match :parameters :path :workspace-id)
                           (-> db :route/match :parameters :query :workspace-id)
                           (-> db :view/workspace :workspace/id))]
      (cond (and workspace-id (contains? pre-workspace-step-paths step-path))
            (let [workspace (entities/find-entity (entities/db->context db) {:workspace/id workspace-id})]
              {:route/push {:route  :view/application-wizard
                            :params {:step step-path}
                            :query  (p/assoc-if query-params
                                                :workspace-id workspace-id
                                                :permit-type  (-> workspace :workspace/permit-type)
                                                :group-id     (-> workspace :domain/authority-group :db/id)
                                                :country      (-> workspace :workspace/municipality :municipality/country)
                                                :municipality (-> workspace :workspace/municipality :municipality/number))}})
            workspace-id
            {:db                                      (assoc db ::wizard-query-params query-params)
             (if replace? :route/replace :route/push) (p/assoc-if
                                                        {:route  :view/application-wizard-workspace
                                                         :params {:step         step-path
                                                                  :workspace-id workspace-id}}
                                                        :query (workspace-util/search-origin-query-params
                                                                 (:route/match db)))}
            :else
            {:route/push {:route  :view/application-wizard
                          :params {:step step-path}
                          :query  query-params}}))))

(rf/reg-sub ::creating-workspace?
  :-> ::creating-workspace?)

(rf/reg-event-fx ::exit-wizard
  (fn [{:keys [db]} [_ {:keys [remove-draft? application]}]]
    {:fx [(when remove-draft?
            [:dispatch [::process/signal
                        application
                        [:process.signal/delete]]])
          [:dispatch [::cleanup (save-tracking/current-workspace-id db)]]
          [:route/push :dashboard]]}))

(rf/reg-sub ::submitting?
  :-> ::submitting?)

(rf/reg-event-fx ::cleanup
  (fn [{:keys [db]} [_ workspace-id]]
    ;; ::visited-step-paths is intentionally kept: leaving the wizard for an in-flow
    ;; detour (e.g. sign-off) must not reset the incomplete/orange tracker markers.
    {:db (dissoc db
                 ::wizard-query-params
                 ::creating-workspace?
                 ::delete-application-modal-open?
                 ::submitting?
                 ::authority-group-municipalities)
     :fx [[:dispatch [::plan-step/cleanup]]
          [:dispatch [::classification-step/cleanup]]
          [:dispatch [::authority-create/cleanup]]
          [:dispatch [::location-step/cleanup]]
          [:dispatch [::parties-step/cleanup]]
          [:dispatch [::req-components/reset-ui-state]]
          [:dispatch [::save-tracking/cleanup workspace-id]]]}))

(rf/reg-event-fx ::redirect-to-workspace
  (fn [{:keys [db]} [_ workspace-id]]
    {:route/replace (p/assoc-if {:route  :view/workspace
                                 :params {:workspace-id workspace-id}}
                                :query (workspace-util/search-origin-query-params (:route/match db)))}))

(rf/reg-event-fx ::redirect-to-wizard-end
  (fn [_ [_ workspace-id]]
    {:route/replace {:route  :view/application-wizard-end
                     :params {:workspace-id workspace-id}}}))

(rf/reg-event-fx ::submit-application
  ;; Terminal action from the summary/payment step. UI-only signals (see
  ;; wizard-nav/local-signal-handlers) are performed locally without touching the
  ;; backend. Everything else is a real FSM command. ::submit-action guarantees only
  ;; signals in that map, or real commands, can reach here.
  (fn [{:keys [db]} [_ application {:keys [signal-id]} workspace-id]]
    (if-let [handler (wizard-nav/local-signal-handlers signal-id)]
      {:dispatch [handler workspace-id]}
      ;; Mark submitting? so guarded-wizard-view doesn't race-redirect on apply-changes.
      {:db       (assoc db ::submitting? true)
       :dispatch [::process/signal application [signal-id]
                  {:on-success [::submit-application-success workspace-id]
                   :on-done    [::submit-application-finally]}]})))

(rf/reg-event-db ::submit-application-finally
  (fn [db [_ status]]
    ; On success ::submitting? remains true until cleared on ::cleanup.
    (if (and (number? status) (<= 200 status 299)) db (dissoc db ::submitting?))))

(rf/reg-event-fx ::submit-application-success
  ;; Route based on the resulting application state:
  ;; - still in pre-submit-payment → advance to the in-wizard payment step
  ;; - any other state (submitted, etc.) → show the wizard end screen
  (fn [{:keys [db]} [_ workspace-id]]
    (let [state (some-> (entities/db->context db)
                        (entities/find-entity {:workspace/id workspace-id})
                        workspace-model/workspace-application
                        :domain/state)]
      (if (= :state.application/pre-submit-payment state)
        ;; Payment completes through shared fee controls, not ::submit-application.
        ;; Release the redirect guard so it can react when payment advances the application.
        {:db (dissoc db ::submitting?)
         :fx [[:dispatch [::open-step {:step-path "payment"}]]]}
        {:fx [[:dispatch [::redirect-to-wizard-end workspace-id]]]}))))

(def ^:private wizard-route-names
  #{:view/application-wizard :view/application-wizard-workspace :view/application-wizard-resume})

;; Controllers stop inside ::on-navigate, so the db already holds the new route match here.
;; The controller passes the source workspace because it can no longer be derived from db.
(rf/reg-event-fx ::cleanup-on-leave
  (fn [{:keys [db]} [_ workspace-id clear-workspace-context?]]
    (when-not (wizard-route-names (get-in db [:route/match :data :name]))
      {:fx (cond-> [[:dispatch [::cleanup workspace-id]]]
             clear-workspace-context?
             (conj [:dispatch [:app.front.view.application/exit-view]]))})))

(rf/reg-sub ::municipality
  :<- [:route/match]
  #(-> % :parameters :query :municipality))

(rf/reg-sub ::municipality-name
  :-> ::municipality-name)

(rf/reg-sub ::step-completed?
  :<- [:route/match]
  :<- [::available-steps]
  :<- [::subs/workspace-id]
  :<- [::subs/application]
  :<- [::plan-step/step-completed?]
  :<- [::classification-step/step-completed?]
  :<- [::location-step/step-completed?]
  :<- [::parties-step/step-completed?]
  :<- [::forms-step/step-completed?]
  :<- [::attachments-step/step-completed?]
  :<- [::payment-step/step-completed?]
  (fn [[match available-steps workspace-id application plan-completed? classification-completed? location-completed?
        parties-completed? forms-completed? attachments-completed? payment-completed?]
       [_ step-path]]
    (let [current-step       (-> match :parameters :path :step)
          current-step-index (p/find-index available-steps {:path current-step})
          step-index         (p/find-index available-steps {:path step-path})
          completed?         (post-workspace-step-completion parties-completed? forms-completed? attachments-completed?)]
      (cond
        (and (contains? pre-workspace-step-paths step-path)
             (not workspace-id))
        (case step-path
          "plan"           plan-completed?
          "classification" classification-completed?
          "location"       location-completed?
          (<= step-index current-step-index))

        ;; Payment is only "complete" once all bills are paid (drives the tracker tick).
        (= step-path "payment")
        payment-completed?

        ;; Once the application has advanced past the summary into pre-submit-payment,
        ;; the summary step is done — show its tracker tick.
        (= step-path "summary")
        (= :state.application/pre-submit-payment (:domain/state application))

        (contains? post-workspace-step-paths step-path)
        (completed? step-path)

        ;; When a workspace exists, all pre-workspace steps are considered visually complete
        ;; (user has already progressed past them).
        :else
        true))))

(rf/reg-sub ::step-reachable?
  :<- [:route/match]
  :<- [::available-steps]
  :<- [::subs/workspace-id]
  :<- [::plan-step/step-completed?]
  :<- [::classification-step/step-completed?]
  :<- [::location-step/step-completed?]
  (fn [[match available-steps workspace-id plan-completed? classification-completed? location-completed?] [_ step-path]]
    (let [current-step       (-> match :parameters :path :step)
          current-step-index (p/find-index available-steps {:path current-step})
          step-index         (p/find-index available-steps {:path step-path})]
      (cond
        (and (contains? pre-workspace-step-paths step-path)
             (not workspace-id))
        (case step-path
          "plan"           plan-completed?
          "classification" classification-completed?
          "location"       location-completed?
          (<= step-index current-step-index))

        (contains? post-workspace-step-paths step-path)
        (if workspace-id
          true
          (<= step-index current-step-index))

        ;; When a workspace exists, all pre-workspace steps are freely navigable
        ;; (user can go back and change prior selections without restriction).
        :else
        true))))

(defn step-locked? [application step-path]
  (and (= step-path "payment")
       (not= :state.application/pre-submit-payment (:domain/state application))))

(defn resume-step-path
  [available-steps application parties-completed? forms-completed? attachments-completed?]
  ;; Resolve where the dashboard "resume draft" link should drop the user: the first
  ;; post-workspace step that still has data preventing submission, or — when everything
  ;; checkable is done — the last unlocked step, ready to continue the submission flow.
  ;; Only steps that own a real completion check participate; summary has no check since
  ;; it's only ever the "all done" landing target.
  (let [post-steps (->> available-steps
                        (filter #(contains? post-workspace-step-paths (:path %)))
                        (map :path))
        completed? (post-workspace-step-completion parties-completed? forms-completed? attachments-completed?)]
    (or (some (fn [path]
                (when (and (contains? completed? path)
                           (not (completed? path)))
                  path))
              post-steps)
        (last (remove #(step-locked? application %) post-steps)))))

(rf/reg-sub ::resume-step-path
  :<- [::available-steps]
  :<- [::subs/application]
  :<- [::parties-step/step-completed?]
  :<- [::forms-step/step-completed?]
  :<- [::attachments-step/step-completed?]
  (fn [[available-steps application parties-completed? forms-completed? attachments-completed?] _]
    (resume-step-path available-steps application parties-completed? forms-completed? attachments-completed?)))

(rf/reg-sub ::step-locked?
  ;; The payment step must not be reachable from the tracker until the user has explicitly
  ;; advanced past the summary via "continue to payment", i.e. until the application leaves
  ;; draft for pre-submit-payment. Other steps are never locked.
  :<- [::subs/application]
  (fn [application [_ step-path]]
    (step-locked? application step-path)))

(rf/reg-sub ::step-incomplete?
  (fn [[_ step-path] _]
    [(rf/subscribe [:route/match])
     (rf/subscribe [::step-visited? step-path])
     (rf/subscribe [::parties-step/step-completed?])
     (rf/subscribe [::forms-step/step-completed?])
     (rf/subscribe [::attachments-step/step-completed?])])
  (fn [[match step-visited? parties-completed? forms-completed? attachments-completed?] [_ step-path]]
    (let [current-step (-> match :parameters :path :step)
          completed?   (post-workspace-step-completion parties-completed? forms-completed? attachments-completed?)]
      (and (not= step-path current-step)
           step-visited?
           (contains? completed? step-path)
           (not (completed? step-path))))))

(rf/reg-sub ::step-next-enabled?
  (fn [[_ step-path] _]
    [(rf/subscribe [::subs/workspace-id])
     (rf/subscribe [::step-completed? step-path])
     (rf/subscribe [::attachments-step/hanging-files?])])
  (fn [[workspace-id step-completed? hanging-files?] [_ step-path]]
    (and (not (and (= "attachments" step-path) hanging-files?))
         (or (#{"summary" "payment"} step-path)
             step-completed?
             (and workspace-id
                  (contains? post-workspace-step-paths step-path))))))

(s/defn location-for-create-app
  "Extracts location data for the create application API call from app-db
   location data (stored by the location step)."
  [{:keys [x y gis-id gis-layer address] :as location} :- property-selector/LocationWizardParams]
  (let [property (select-keys location property-model/property-creation-keys)]
    (p/assoc-if
      {:geometry-point [x y]}
      :property             (not-empty property)
      :address              (when (nil? (address-model/address-checker address)) address)
      :gis-id               gis-id
      :gis-layer            (some-> gis-layer keyword)
      :geometry             (:geometry location)
      :location/description (when (empty? property)
                              (:location/description location)))))

(defn- location-data-with-manual-overrides
  "Merges manual input overrides (property/id, property/legal, location/description)
   and manual address into the base location data from the location step.
   For PW without a selected property, only :location/description is merged so autosave
   dispatches unlink + edit-workspace-location instead of change-primary-property."
  [db]
  (let [location-data    (::location-step/location-data db)
        manual-input     (::location-step/manual-input db)
        manual-address   (::location-step/manual-address db)
        permit-type      (-> db :route/match :parameters :query :permit-type)
        propertyless?    (location-step/propertyless-manual-location?
                           (ptc-model/permit-types-with-row-layers permit-type) location-data)
        manual-property? (and (:enabled? manual-input) (not propertyless?))
        manual-keys      (if propertyless?
                           [:location/description]
                           [:property/id :property/legal :location/description])
        merged-location  (cond-> location-data
                           (:enabled? manual-input)
                           (merge (select-keys (:values manual-input) manual-keys)))]
    (cond-> merged-location

      ;; Keep the property payload even when an optional PID is cleared in the manual form.
      manual-property?
      (assoc :property/type (or (:property/type location-data) :property.type/property))

      ;; Without a PID, use manual creation so the backend generates one instead of verifying the old GIS identity.
      (and manual-property? (not (str-util/blank->nil (:property/id merged-location))))
      (dissoc :property/id :property/integration-id :gis-id :gis-layer)

      manual-address
      (assoc :address manual-address))))

(rf/reg-event-fx ::create-application
  (fn [{:keys [db]} [_ next-step-path location-not-required? create-pre-consultation? authority?]]
    (let [query-params        (-> db :route/match :parameters :query)
          category-id         (::classification-step/category-id db)
          work-type-ids       (::classification-step/work-type-ids db)
          work-target-id      (::classification-step/work-target-id db)
          project-id          (::plan-step/project-id db)
          project-name        (::plan-step/project-name db)
          description-of-work (::plan-step/description-of-work db)
          uploaded-docs       (::plan-step/uploaded-documents db)
          langfuse-session-id (::plan-step/langfuse-session-id db)
          permit-type         (:permit-type query-params)
          with-project?       (not (project-model/permit-types-without-project permit-type))
          associate-units?    (::location-step/associate-units? db)
          selected-unit-ids   (when associate-units?
                                (not-empty (::location-step/selected-existing-unit-ids db)))
          new-units           (when associate-units?
                                (some->> (::units-editor/pending-new-property-units db)
                                         not-empty
                                         (mapv (fn [{:keys [unit-designator identifier]}]
                                                 (p/assoc-if {:unit-designator-id (:db/id unit-designator)}
                                                             :identifier identifier)))))
          app-data            (p/assoc-if
                                (merge
                                  (when-not location-not-required?
                                    (-> db location-data-with-manual-overrides location-for-create-app))
                                  {:country      (:country query-params)
                                   :municipality (:municipality query-params)
                                   :permit-type  permit-type
                                   :category-id  category-id}
                                  (authority-create/authority-create-application-data db authority?))
                                :create-pre-consultation? create-pre-consultation?
                                :work-type-ids            work-type-ids
                                :work-target-id           work-target-id
                                :project/id               (when with-project? project-id)
                                :project/name             (when with-project? (str-util/blank->nil project-name))
                                :description-of-work      (str-util/blank->nil description-of-work)
                                :uploaded-documents       (some->> uploaded-docs
                                                                   (mapv #(select-keys % [:file-id :filename :content-type])))
                                :langfuse-session-id         langfuse-session-id
                                :selected-property-unit-ids  selected-unit-ids
                                :new-property-units          new-units)]
      {:db       (cond-> db
                   (and (seq uploaded-docs) langfuse-session-id) (assoc ::creating-workspace? true))
       ;; Monitored command marks :application/create as processing, which disables the wizard's
       ;; Next button while the create is in flight, preventing double-submit (see WIN unique-conflict).
       :dispatch [:comm/command-monitored
                  {:command          :application/create
                   :data             app-data
                   :on-success       [::create-application-success next-step-path create-pre-consultation?]
                   :finally-dispatch [::create-application.finally]}]})))

(rf/reg-event-db ::create-application.finally
  (fn [db _]
    (assoc db ::creating-workspace? false)))

(rf/reg-event-fx ::create-application-success
  (fn [_ [_ step-path create-pre-consultation? status response]]
    (let [ws-id (entities/property-of-some-in-apply-changes :workspace/id response)]
      {:fx [[:dispatch [::entities/apply-changes status response]]
            (if create-pre-consultation?
              [:route/push {:route  :view/application-wizard-end
                            :params {:workspace-id ws-id}
                            :query  {:type "pre-consultation"}}]
              [:dispatch [::open-step (util/mmap step-path ws-id)]])]})))

(defn- location-update-signal [location]
  (if (:property location)
    [:process.signal/change-primary-property
     (p/assoc-if location :allow-missing-address? (nil? (:address location)))]
    [:process.signal/edit-workspace-location
     (p/assoc-if {:coordinates (:geometry-point location)}
                 :description (:location/description location))]))

(defn- current-wizard-workspace [db]
  (when-let [workspace-id (-> db :route/match :parameters :query :workspace-id)]
    (entities/find-entity (entities/db->context db) {:workspace/id workspace-id})))

(rf/reg-event-fx ::autosave-location
  (fn [{:keys [db]} _]
    (let [location      (-> db location-data-with-manual-overrides location-for-create-app)
          workspace     (current-wizard-workspace db)
          target-id     (some-> workspace workspace-model/workspace-application :db/id)
          propertyless? (and (nil? (:property location)) (:workspace/primary-property workspace))]
      (when target-id
        (if propertyless?
          (let [operation-id (random-uuid)]
            {:dispatch [::process/signal
                        target-id
                        [:process.signal/unlink-primary-property]
                        {:on-success   [::autosave-propertyless-location-after-unlink operation-id]
                         :track-save?  true
                         :operation-id operation-id
                         :chained?     true}]})
          {:dispatch [::process/signal
                      target-id
                      (location-update-signal location)
                      {:track-save? true}]})))))

(rf/reg-event-fx ::autosave-propertyless-location-after-unlink
  (fn [{:keys [db]} [_ operation-id]]
    (if-let [target-id (some-> db current-wizard-workspace workspace-model/workspace-application :db/id)]
      {:dispatch [::process/signal
                  target-id
                  (-> db location-data-with-manual-overrides location-for-create-app location-update-signal)
                  {:track-save?  true
                   :operation-id operation-id}]}
      {:dispatch [::save-tracking/save-failed operation-id]})))

(rf/reg-event-db ::set-delete-application-modal-open
  (fn [db [_ open?]]
    (assoc db ::delete-application-modal-open? open?)))

(rf/reg-sub ::delete-application-modal-open?
  (fn [db _]
    (get db ::delete-application-modal-open?)))

(rf/reg-event-db ::set-analysis-in-flight-modal-open
  (fn [db [_ open?]]
    (assoc db ::analysis-in-flight-modal-open? open?)))

(rf/reg-sub ::analysis-in-flight-modal-open?
  (fn [db _]
    (get db ::analysis-in-flight-modal-open?)))

(defn- wizard-submit-action [{:keys [tr application workspace-id]}]
  (let [resolved-action    (<sub [::subs/submit-action])
        submit-signal      (:signal-id resolved-action)
        submit-processing? (<sub [::process/signal-processing? submit-signal])
        disabled?          (or submit-processing?
                               (not submit-signal)
                               (some-> resolved-action :status (= :disabled)))]
    {:signal-id      submit-signal
     :process-target application
     :workspace-id   workspace-id
     :status         (:status resolved-action)
     :text           (tr [(or submit-signal :process.signal/submit)])
     :disabled?      disabled?
     :processing?    submit-processing?
     :on-click       (when submit-signal
                       #(>evt [::submit-application application resolved-action workspace-id]))}))

(defn- navigation-buttons [tr available-steps step-index]
  (let [first-step?                    (= step-index 0)
        last-step?                     (= step-index (dec (count available-steps)))
        current-step                   (nth available-steps step-index)
        current-step-path              (:path current-step)
        step-next-enabled?             (<sub [::step-next-enabled? current-step-path])
        prev-path                      (when-not first-step?
                                         (->> step-index dec (nth available-steps) :path))
        next-path                      (when-not last-step?
                                         (->> step-index inc (nth available-steps) :path))
        desktop?                       (<sub [::media-query/breakpoint-matches? :md])
        creating-application?          (<sub [:comm/command-processing? :application/create])
        saving-location?               (<sub [::process/signal-processing?
                                              :process.signal/change-primary-property
                                              :process.signal/edit-workspace-location
                                              :process.signal/unlink-primary-property])
        saving-classification?         (<sub [::process/signal-processing? :process.signal/set-ws-config-path])
        workspace-id                   (<sub [::subs/workspace-id])
        authority?                     (<sub [::subs/authority-user?])
        location-not-required?         (<sub [::location-not-required?])
        create-pre-consultation?       (<sub [::classification-step/create-pre-consultation?])
        needs-workspace?               (and (nil? workspace-id)
                                            (or (= next-path (-> post-workspace-steps first :path))
                                                (and create-pre-consultation?
                                                     (= current-step-path (-> available-steps last :path)))))
        create-pre-consultation-step?  (and needs-workspace? create-pre-consultation?)
        application                    (<sub [::subs/application])
        app-in-payment-phase?          (= :state.application/pre-submit-payment (:domain/state application))
        ;; Summary stops being the terminal step once the application has moved to the payment
        ;; phase; from then on payment is terminal and summary navigates forward to it.
        ;; `last-step?` keeps summary terminal if there is somehow no payment step after it.
        submit-step?                   (and (#{"summary" "payment"} current-step-path)
                                            (or last-step? (not app-in-payment-phase?)))
        delete-application-modal-open? (<sub [::delete-application-modal-open?])
        analysis-in-flight-modal-open? (<sub [::analysis-in-flight-modal-open?])
        analysis-in-flight?            (<sub [::summary-step/rule-check-in-flight?])
        confirm-forward?               (and (= "summary" current-step-path) analysis-in-flight?)
        exit-action                    {:text     (tr [:application-wizard/exit-to-dashboard])
                                        :kind     :tertiary
                                        :on-click #(>evt [::exit-wizard {:remove-draft? false}])}
        cancel-action                  {:text     (tr [:application-wizard/cancel-application])
                                        :kind     :ghost
                                        :on-click #(if application
                                                     (>evt [::set-delete-application-modal-open true])
                                                     (>evt [::exit-wizard {:remove-draft? false}]))}
        sign-off-visit?                (and (= "summary" current-step-path)
                                            application
                                            (<sub [::subs/sign-off-done?])
                                            (not= :process.signal/sign-off-view
                                                  (:signal-id (<sub [::subs/submit-action])))
                                            (<sub [::process/signal-permitted?
                                                   application
                                                   [:process.signal/sign-off-view-visit]]))
        sign-off-visit-action          {:text     (tr [:process.signal/sign-off-view-visit])
                                        :kind     :tertiary
                                        :on-click #(>evt [::wizard-nav/redirect-to-sign-off workspace-id])}
        leading-actions                (cond-> [cancel-action]
                                         application
                                         (conj exit-action)
                                         sign-off-visit?
                                         (conj sign-off-visit-action))
        open-next-event                [::open-step {:step-path next-path}]
        next-action                    (cond
                                         submit-step?
                                         (wizard-submit-action {:tr           tr
                                                                :application  application
                                                                :workspace-id workspace-id})

                                         needs-workspace?
                                         {:text     (if create-pre-consultation-step?
                                                      (tr [:application-wizard/create-pre-consultation])
                                                      (tr [:application-wizard/next]))
                                          :on-click #(>evt [::create-application
                                                            next-path
                                                            location-not-required?
                                                            create-pre-consultation?
                                                            authority?])}

                                         :else
                                         {:text     (tr [:application-wizard/next])
                                          :on-click #(>evt open-next-event)})
        forward!                       #((:on-click next-action))
        primary-actions                [(when-not first-step?
                                          {:text     (tr [:application-wizard/previous])
                                           :kind     :tertiary
                                           :on-click #(>evt [::open-step {:step-path prev-path}])})
                                        {:text      (:text next-action)
                                         :kind      :primary
                                         :disabled? (or (not step-next-enabled?)
                                                        saving-location?
                                                        saving-classification?
                                                        creating-application?
                                                        (:disabled? next-action))
                                         :on-click  #(if confirm-forward?
                                                       (>evt [::set-analysis-in-flight-modal-open true])
                                                       (forward!))}]]
    [:<>
     [ui/confirm-modal
      {:id              "delete-application-modal"
       :tr              tr
       :open?           delete-application-modal-open?
       :dangerous?      true
       :on-close        #(>evt [::set-delete-application-modal-open false])
       :modal-title     (tr [:application-wizard/application-cancel-modal-title])
       :yes-button-text (tr [:application-wizard/application-cancel-modal-yes-button])
       :no-button-text  (tr [:application-wizard/application-cancel-modal-no-button])
       :on-yes-click    #(>evt [::exit-wizard {:remove-draft? true
                                               :application   application}])}
      (tr [:application-wizard/application-cancel-modal-body])]
     [ui/confirm-modal
      {:id              "analysis-in-flight-modal"
       :tr              tr
       :open?           analysis-in-flight-modal-open?
       :on-close        #(>evt [::set-analysis-in-flight-modal-open false])
       :modal-title     (tr [:application-wizard/cloudcheck-analysis-in-flight-modal-title])
       :yes-button-text (tr [:application-wizard/cloudcheck-analysis-in-flight-modal-yes-button])
       :no-button-text  (tr [:application-wizard/cloudcheck-analysis-in-flight-modal-no-button])
       :on-yes-click    forward!}
      (tr [:application-wizard/cloudcheck-analysis-in-flight-modal-body])]
     (if desktop?
       [:div.p-6.flex.flex-row.items-center.justify-end
        [action-buttons/action-buttons
         {:buttons  (concat leading-actions primary-actions)
          :loading? (or creating-application? saving-location? saving-classification?)}]]
       [mobile-bar/mobile-bottom-action-bar
        (util/mmap {:data-cy               "wizard-mobile-action-bar"
                    :overflow-menu-data-cy "wizard-overflow-menu"}
                   leading-actions primary-actions tr)])]))

(defn- ai-populated-banner [tr]
  [:div.mb-6.flex
   {:role "status"}
   [:div.flex.items-center.justify-center.rounded-l.bg-ui-violet-100.px-3
    (cc/icon {:icon        i/novacloud
              :description ""
              :aria-hidden true
              :fill        "white"
              :width       "26"})]
   [:div.flex.items-center.rounded-r.p-4.bg-ui-violet-10
    (tr [:application-wizard/ai-populated-banner])]])

(defn- step-header [tr {:keys [description-tr description-view]}]
  (let [save-tracking-status (<sub [::save-tracking/status])]
    [:div.mb-3.flex.flex-col-reverse.md:grid.md:items-start.gap-4
     {:class "grid-cols-[minmax(0,1fr)_auto]"}
     [:div.min-w-0.text-ui-night-sky-100.leading-relaxed
      [:p.m-0.p-0.md:pb-6
       (if description-view
         [description-view tr]
         (tr description-tr))]]
     (when-not (= :idle save-tracking-status)
       [save-status/indicator {:tr         tr
                               :class-name "justify-self-end"}])]))

(defn- wizard-view [{:keys [step permit-type group-id]}]
  (let [tr                     (<sub [::subs/tr])
        lang                   (<sub [:language])
        available-steps        (<sub [::available-steps])
        wizard-config-loading? (<sub [:comm/command-processing? :municipality/wizard-config-by-authority-group])
        creating-workspace?    (<sub [::creating-workspace?])
        icon-path              (ptc-model/permit-type->icon-path permit-type)
        step-index             (p/find-index available-steps {:path step})
        step-config            (some->> step-index (nth available-steps))
        desktop?               (<sub [::media-query/breakpoint-matches? :md])
        entities-context       (<sub [::entities/context])
        workspace              (<sub [::subs/workspace])
        authority?             (<sub [::subs/authority-user?])
        analysis-completed?    (<sub [::plan-step/analysis-completed?])
        permit-type-config     (entities/find-entity entities-context {:group.config/group  group-id
                                                                       :group.config/target permit-type})
        permit-type-name       (or (some-> permit-type-config
                                           :group.config/names
                                           (i18n-model/get-text-from-entities-or-map lang))
                                   (tr [permit-type]))]
    [:div.cloudpermit-page
     (if (or wizard-config-loading? (not step-index))
       [section/section-skeleton]
       [:<>
        [:div.flex.flex-col.md:flex-row.md:items-center.md:justify-between.my-4.gap-4
         [:div.flex.items-center.min-w-0.flex-1
          (when icon-path
            [:img.mr-2.h-8.flex-shrink-0 {:src icon-path :alt permit-type-name}])
          [:h1.my-4.break-words (str permit-type-name " "
                                     (if (<sub [::subs/renewal-workspace?])
                                       (tr [:application-wizard/renewal])
                                       (tr [:application-wizard/application])))]]
         (when (and authority? workspace)
           [wizard-nav/page-switcher-view {:tr           tr
                                           :workspace-id (:workspace/id workspace)
                                           :current-view :wizard}])]
        [:div.bg-white.rounded.border.border-solid.border-gray-400
         [step-tracker/step-tracker (util/mmap {:current-step-index  step-index
                                                :steps               (->> available-steps
                                                                          (map #(select-keys % [:path :label-tr])))
                                                :open-step-evt       ::open-step
                                                :step-completed-sub  ::step-completed?
                                                :step-incomplete-sub ::step-incomplete?
                                                :step-reachable-sub  ::step-reachable?
                                                :step-locked-sub     ::step-locked?}
                                               tr)]
         (if creating-workspace?
           [:div.p-6.flex.flex-col.items-center.justify-center.gap-4
            [:p.text-center (tr [:application-wizard/creating-workspace-and-analyzing])]
            (cc/loading-spinner {:with-overlay false})]
           [:<>
            [:div.p-6.border-b.border-t.border-solid.border-gray-400
             (when (or (:description-tr step-config) (:description-view step-config))
               [step-header tr step-config])
             (when (and (not= "payment" step) analysis-completed?)
               [ai-populated-banner tr])
             [(:view step-config) tr]]
            [navigation-buttons tr available-steps step-index]])]
        (when-not desktop?
          ;; Spacer to prevent the fixed mobile bottom bar from covering content.
          [:div {:class "h-20" :aria-hidden true} "\u00a0"])])]))

(defn- guarded-wizard-view [_wizard-opts]
  (let [state           (atom {:workspace-id nil :workspace nil :redirected? false :submitting? false})
        maybe-redirect! (fn [& _]
                          (let [{:keys [workspace-id workspace redirected? submitting?]} @state]
                            (when (and workspace-id
                                       workspace
                                       (not redirected?)
                                       (not submitting?)
                                       (not (wizard-nav/wizard-allowed-for-workspace? workspace)))
                              (swap! state assoc :redirected? true)
                              (>evt (if (wizard-nav/application-submitted? workspace)
                                      [::redirect-to-wizard-end workspace-id]
                                      [::redirect-to-workspace workspace-id])))))]
    (r/create-class
      {:display-name         "application-wizard-guard"
       :component-did-mount  maybe-redirect!
       :component-did-update maybe-redirect!
       :reagent-render       (fn [wizard-opts]
                               (let [workspace-id (<sub [::subs/workspace-id])
                                     ctx          (<sub [::entities/context])
                                     submitting?  (<sub [::submitting?])
                                     workspace    (when workspace-id
                                                    (entities/find-entity ctx {:workspace/id workspace-id}))
                                     invalid?     (and workspace-id
                                                       workspace
                                                       (not submitting?)
                                                       (not (wizard-nav/wizard-allowed-for-workspace? workspace)))]
                                 (swap! state assoc :workspace-id workspace-id :workspace workspace :submitting? submitting?)
                                 (if invalid?
                                   [:div.cloudpermit-page
                                    [section/section-skeleton]]
                                   [wizard-view wizard-opts])))})))

;; View for the pre-workspace route (plan, classification, location)
(defn- pre-workspace-view [{{{:keys [step]}                 :path
                             {:keys [permit-type group-id]} :query} :parameters}]
  [guarded-wizard-view {:step step :permit-type permit-type :group-id group-id}])

;; View for the workspace route (forms, parties, attachments, summary)
(defn- workspace-wizard-view [{{{:keys [step]} :path} :parameters}]
  (if (or (<sub [::ws-subs/loading?])
          (<sub [::ws-subs/loading-config?])
          (and (nil? (<sub [::process/current-ws-process-states]))
               (<sub [:comm/command-processing? :group/process-states])))
    [:div.cloudpermit-page
     [section/section-skeleton]]
    (let [permit-type (<sub [::ws-subs/permit-type])
          group-id    (<sub [::ws-subs/authority-group-id])]
      [guarded-wizard-view {:step step :permit-type permit-type :group-id group-id}])))

;; Route for pre-workspace steps (plan, classification, location)
(def route
  {:name             :view/application-wizard
   :view             pre-workspace-view
   :view-sub         [::subs/view-data]
   :hide-breadcrumb? true
   :hide-footer?     true
   :track-saves?     true
   :parameters       {:path  {:step (apply s/enum pre-workspace-step-paths)}
                      :query (st/optional-keys
                               (st/merge
                                 {:permit-type         (apply s/enum ptc-model/application-types)
                                  :origin              s/Str
                                  :group-id            s/Int
                                  :country             s/Keyword
                                  :municipality        s/Str
                                  :workspace-id        s/Str
                                  :manual-entry?       s/Bool
                                  :manual-values       s/Str
                                  :project             s/Int
                                  :project-name        s/Str
                                  :description-of-work s/Str
                                  :category            s/Int
                                  :work-types          [s/Int]
                                  :work-target         s/Int}
                                 ;; :municipality is declared above as the route's entry parameter.
                                 (st/dissoc property-selector/LocationWizardParams :municipality)))}
   :controllers      [;; Entry controller handles new applications using their authority group and permit type.
                      ;; Municipality selection is local state and must not reload wizard configuration.
                      ;; The workspace-id path (resume existing draft) is handled by the second controller.
                      ;; Redirects to dashboard only when no valid entry can be established.
                      {:parameters {:query [:workspace-id :permit-type :group-id]}
                       :start      (fn [{{:keys [workspace-id permit-type group-id]} :query}]
                                     (cond (and (not workspace-id) permit-type group-id)
                                           (do (>evt [::store-query-params])
                                               (>evt [::init group-id permit-type]))

                                           (not workspace-id)
                                           (>evt [:route/push :dashboard])))}
                      {:parameters {:query [:workspace-id]}
                       :start      (fn [{{:keys [workspace-id]} :query}]
                                     (when workspace-id
                                       (>evt [::fetch-and-backfill-workspace workspace-id])))
                       :stop       (fn [{{:keys [workspace-id]} :query}]
                                     (>evt [::cleanup-on-leave workspace-id true]))}
                      ;; Pre-select the project the user came from (e.g. project view -> new workspace).
                      {:parameters {:query [:project]}
                       :start      (fn [{{:keys [project]} :query}]
                                     (when project
                                       (>evt [::plan-step/select-project project])))}
                      {:parameters {:query [:project-name :description-of-work]}
                       :start      (fn [{:keys [query]}]
                                     (when (some query [:project-name :description-of-work])
                                       (>evt [::plan-step/restore-from-query query])))}
                      {:parameters {:query [:category :work-types :work-target]}
                       :start      (fn [{:keys [query]}]
                                     (when (:category query)
                                       (>evt [::classification-step/restore-from-query query])))}
                      {:parameters {:query (into [:manual-entry? :manual-values]
                                                 (remove #{:municipality}
                                                         property-selector/location-keys-without-project))}
                       :start      (fn [{:keys [query]}]
                                     (when (or (:manual-entry? query)
                                               (and (:x query) (:y query))
                                               (some query [:location/description :property/legal]))
                                       (>evt [::location-step/restore-from-query query])))}
                      {:parameters {:path [:step]}
                       :start      (fn [{{:keys [step]} :path}]
                                     (>evt [::mark-step-visited step])
                                     (>evt [::save-tracking/step-entered])
                                     (when (= step "location")
                                       (>evt [::location-step/enter-step])))}]})

;; Route for post-workspace steps (forms, parties, attachments, summary).
;; Nested under workspace/:workspace-id — parent controller fetches workspace data.
(def workspace-wizard-route
  {:name             :view/application-wizard-workspace
   :view             workspace-wizard-view
   :hide-breadcrumb? true
   :hide-footer?     true
   :track-saves?     true
   :view-sub         [::subs/view-data]
   :parameters       {:path {:step (apply s/enum post-workspace-step-paths)}}
   :controllers      [{:parameters {:path [:step]}
                       :start      (fn [{{:keys [step]} :path}]
                                     (>evt [::mark-step-visited step])
                                     (>evt [::save-tracking/step-entered])
                                     (cond
                                       (= step "parties")
                                       (>evt [::parties-step/enter-step])

                                       (= step "forms")
                                       (>evt [::form-subs/enter-forms-step])

                                       (= step "summary")
                                       (>evt [::summary-step/maybe-fetch-permit-estimate])

                                       (= step "payment")
                                       (>evt [::payment-step/enter-step])))
                       :stop       (fn [{{:keys [step]} :path}]
                                     (case step
                                       "attachments" (>evt [::req-components/reset-ui-state])
                                       "payment"     (>evt [::payment-step/leave-step])
                                       nil))}
                      {:parameters {:path [:workspace-id]}
                       :stop       (fn [{{:keys [workspace-id]} :path}]
                                     (>evt [::cleanup-on-leave workspace-id]))}]})

;; Sentinel "resume" target for the dashboard draft link. The parent workspace route loads the
;; workspace entities; once they are in, we redirect to the first step with missing data.
;; Uses its own /wizard-resume segment so it doesn't conflict with the /wizard/:step route.
(defn- resume-view [_]
  (let [;; Subscriptions are read in render (a reactive context); lifecycle hooks act on these
        ;; captured values so we don't subscribe outside a reactive context.
        state           (atom {:loading? true :target nil :workspace-id nil :workspace nil :redirected? false})
        maybe-redirect! (fn [& _]
                          (let [{:keys [loading? target workspace-id workspace redirected?]} @state]
                            (cond
                              (and workspace-id
                                   workspace
                                   (not redirected?)
                                   (not (wizard-nav/wizard-allowed-for-workspace? workspace)))
                              (do (swap! state assoc :redirected? true)
                                  (>evt (if (wizard-nav/application-submitted? workspace)
                                          [::redirect-to-wizard-end workspace-id]
                                          [::redirect-to-workspace workspace-id])))

                              (and (not loading?) target (not redirected?))
                              (do (swap! state assoc :redirected? true)
                                  (>evt [::open-step {:step-path target :replace? true}])))))]
    (r/create-class
      {:display-name         "application-wizard-resume"
       :component-did-mount  maybe-redirect!
       :component-did-update maybe-redirect!
       :reagent-render       (fn [_]
                               (let [workspace-id (<sub [::subs/workspace-id])
                                     ctx          (<sub [::entities/context])
                                     workspace    (when workspace-id
                                                    (entities/find-entity ctx {:workspace/id workspace-id}))]
                                 (swap! state assoc
                                        :loading?    (<sub [::ws-subs/loading?])
                                        :target      (<sub [::resume-step-path])
                                        :workspace-id workspace-id
                                        :workspace    workspace))
                               [:div.cloudpermit-page
                                [section/section-skeleton]])})))

;; Route for the resume sentinel — also nested under workspace/:workspace-id.
(def workspace-wizard-resume-route
  {:name             :view/application-wizard-resume
   :view             resume-view
   :hide-breadcrumb? true
   :track-saves?     true})
